import { useState } from "react";
import type { PendingAccess } from "@/journey/types";

/**
 * Wireframe 3 · Permission. Explains the request in plain language immediately
 * before it happens. `Allow` calls into the runtime synchronously so the click
 * still counts as user activation, which `XRSystem.requestSession` requires.
 */
export function PermissionScreen({
  access,
  onAllow,
  onSkip,
}: {
  access: PendingAccess;
  onAllow: () => Promise<void>;
  onSkip: () => void;
}) {
  const [requesting, setRequesting] = useState(false);

  return (
    <section className="panel permission-panel" data-testid="permission">
      <span className="permission-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M3 8.5A2.5 2.5 0 0 1 5.5 6h1.7l1.1-1.8A1 1 0 0 1 9.2 3.7h5.6a1 1 0 0 1 .9.5L16.8 6h1.7A2.5 2.5 0 0 1 21 8.5v8A2.5 2.5 0 0 1 18.5 19h-13A2.5 2.5 0 0 1 3 16.5z" />
          <circle cx="12" cy="12" r="3.4" />
        </svg>
      </span>
      <h2>Allow camera access</h2>
      <p className="panel-note">
        We use your camera to place 3D objects in your space. Nothing is recorded or stored.
      </p>
      {/*
        Warn about the second prompt. In AR the browser shows its own sheet
        straight after this one, and an unexplained second dialog is where
        visitors bail.
      */}
      {/* {access === "webxr" ? (
        <p className="panel-hint">Your browser will ask to start AR next.</p>
      ) : null} */}
      <button
        className="button-primary"
        type="button"
        disabled={requesting}
        aria-busy={requesting}
        data-ui-control
        data-testid="permission-allow"
        onClick={() => {
          setRequesting(true);
          void onAllow().finally(() => setRequesting(false));
        }}
      >
        <span>{requesting ? "Waiting…" : "Allow"}</span><i aria-hidden="true">→</i>
      </button>
      <button className="button-quiet" type="button" onClick={onSkip} data-ui-control data-testid="permission-skip">
        Not now
      </button>
    </section>
  );
}
